import { useStore } from '@/store'
import './codeInput.scss'
import {
  FormEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react'

interface CodeInputProps {
  readOnly?: boolean
  value?: string
}
export default function CodeInput(props: CodeInputProps) {
  const inputElem = useRef<HTMLInputElement>(null)
  const { state, dispatch } = useStore()
  const playerType = state.login.userInfo.playerType
  const playerId = state.login.userInfo.playerId
  const isBinded = state.login.userInfo.isBinded
  const code = state.code
  const oldValue = useRef(code)
  const recordSelectionStart = useRef(code.length + 1)
  const initialized = useRef(false)
  const readonlyValue = useMemo(
    () => (isBinded ? props.value : '*********'),
    [isBinded]
  )
  const readonlyType = useMemo(
    () => (isBinded ? 'text' : 'password'),
    [isBinded]
  )

  const setCode = (val: string) => {
    dispatch({
      type: 'setCode',
      data: {
        code: val,
      },
    })
  }

  useEffect(() => {
    if (playerType === 2) return

    if (isBinded && !initialized.current) {
      initialized.current = true
      setCode(code || playerId)
    }
  }, [isBinded, playerId, code])

  useEffect(() => {
    const input = inputElem.current
    const selectionStart = recordSelectionStart.current

    if (oldValue.current === code) {
      _handle()
      return
    }

    oldValue.current = code
    _handle()

    function _handle() {
      if (!input || typeof selectionStart !== 'number') return

      input.setSelectionRange(selectionStart, selectionStart)
    }
  }, [code])

  const handle = useCallback<FormEventHandler<HTMLInputElement>>(
    (e) => {
      if (inputElem.current == null) return

      const target = e.target as HTMLInputElement

      const newValue = format(target.value)

      if (newValue === code) {
        setTimeout(() => {
          inputElem.current.setSelectionRange(
            recordSelectionStart.current,
            recordSelectionStart.current
          )
        }, 1)
        return
      }

      recordSelectionStart.current = getSelectionStart()
      setCode(newValue)

      function format(val: unknown) {
        if (typeof val === 'string') {
          let str = val.replace(/[^a-z0-9]/gi, '').toUpperCase()
          return str.slice(0, 9)
        }
        return ''
      }

      function getSelectionStart() {
        const input = inputElem.current
        const selectionStart: number | null = input
          ? input.selectionStart
          : null
        return selectionStart
      }
    },
    [code]
  )

  return !props.readOnly ? (
    <input
      ref={inputElem}
      className="code-input"
      type="text"
      readOnly={true}
      value={code}
      onInput={handle}
    />
  ) : (
    <input
      ref={inputElem}
      className="code-input"
      readOnly={true}
      type={readonlyType}
      value={readonlyValue}
    />
  )
}
